import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, MessageSquare, Trash2, UserRound, X } from "lucide-react";
import api from "@/lib/api";
import ProblemClarification from "@/components/ProblemClarification";
import ProblemProposals from "@/components/ProblemProposals";
import AgreementScheduling from "@/components/AgreementScheduling";
import VideoConsultationEntry from "@/components/video/VideoConsultationEntry";
import RequestScopeAccordion from "@/components/customer/RequestScopeAccordion";
import { getCustomerRequestStatus, REQUEST_STATUS_CLASSES } from "@/lib/requestStatus";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import ReviewPrompt from "@/components/ReviewPrompt";
import CancellationAndSupport from "@/components/CancellationAndSupport";

export default function CustomerRequestDetail() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [problem, setProblem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedExpertId, setSelectedExpertId] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [error, setError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const clarificationSectionRef = useRef(null);
  const handledChatTargetRef = useRef("");
  const requestedChat = searchParams.get("chat");

  const reload = async () => {
    try {
      const [problemResponse, threadResponse] = await Promise.all([
        api.get(`/problems/${pid}`),
        api.get(`/problems/${pid}/clarification-threads`),
      ]);
      setProblem(problemResponse.data);
      const nextThreads = threadResponse.data || [];
      setThreads(nextThreads);
      setSelectedExpertId((current) => (
        nextThreads.some((thread) => thread.expert_user_id === current)
          ? current
          : nextThreads[0]?.expert_user_id || ""
      ));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Forespørgslen kunne ikke hentes.");
    }
  };

  useEffect(() => {
    handledChatTargetRef.current = "";
    if (requestedChat) { setSelectedExpertId(requestedChat); setChatOpen(true); }
    reload();
    api.get("/categories").then((response) => setCategories(response.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, requestedChat]);

  useEffect(() => {
    const refreshReadState = (event) => {
      if (event.detail?.problemId === pid) reload();
    };
    window.addEventListener("messages:read", refreshReadState);
    return () => window.removeEventListener("messages:read", refreshReadState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  useEffect(() => {
    if (!problem || !requestedChat || !chatOpen || selectedExpertId !== requestedChat) return;
    if (!threads.some((thread) => thread.expert_user_id === requestedChat)) return;
    const targetKey = pid + ":" + requestedChat;
    if (handledChatTargetRef.current === targetKey) return;
    handledChatTargetRef.current = targetKey;
    window.requestAnimationFrame(() => {
      clarificationSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      clarificationSectionRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
    });
  }, [chatOpen, pid, problem, requestedChat, selectedExpertId, threads]);

  const categoryName = useMemo(
    () => categories.find((category) => category.id === problem?.category_id)?.name,
    [categories, problem?.category_id],
  );
  const selectedThread = threads.find((thread) => thread.expert_user_id === selectedExpertId);
  const latestThread = selectedThread || threads[0];
  const unreadCount = threads.reduce((sum, thread) => sum + (thread.unread_count || 0), 0);
  const openChat = async (expertId = selectedExpertId || latestThread?.expert_user_id) => {
    if (expertId) {
      setSelectedExpertId(expertId);
      await api.post(`/problems/${pid}/clarifications/read`, null, { params: { expert_user_id: expertId } }).catch(() => {});
      await reload();
    }
    setChatOpen(true);
  };
  const status = getCustomerRequestStatus({
    ...problem,
    clarification_unanswered: unreadCount > 0,
  });
  const agreementSelected = ["offer_accepted", "completed"].includes(problem?.status);
  const paymentDone = problem?.payment_status === "paid";
  const scheduleDone = problem?.consultation_schedule_state === "confirmed";
  const completed = problem?.status === "completed";
  const hasNewResponse = !agreementSelected && (unreadCount > 0 || (problem?.proposal_count || 0) > 0);

  const deleteRequest = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`/problems/${pid}`);
      navigate("/dashboard", { replace: true });
    } catch (requestError) {
      setDeleteError(requestError.response?.data?.detail || "Forespørgslen kunne ikke slettes.");
      setDeleting(false);
    }
  };

  const requestOtherTime = (proposal) => {
    setSelectedExpertId(proposal.expert_user_id);
    setChatOpen(true);
    window.setTimeout(() => {
      clarificationSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  if (error) {
    return <div className="mx-auto max-w-[1120px] px-4 py-12"><div className="card-outline p-6 text-[#991B1B]">{error}</div></div>;
  }
  if (!problem) return <PageSkeleton cards={3}/>;

  return (
    <div className="mx-auto max-w-[1120px] px-4 py-7 sm:px-6 sm:py-9">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-brand-body hover:text-brand-green">
        <ArrowLeft size={15}/> Tilbage til mine forespørgsler
      </Link>

      <header className="mt-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="font-display text-[28px] font-semibold tracking-[-0.02em] text-brand-ink">{problem.title}</h1>
            <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${REQUEST_STATUS_CLASSES[status.tone]}`}>{status.label}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-xs text-brand-muted">
            {categoryName && <span>{categoryName}</span>}
            {categoryName && <span>·</span>}
            <span>Indsendt {new Date(problem.created_at).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" })}</span>
          </div>
        </div>
        {!["offer_accepted", "completed"].includes(problem.status) && problem.agreement_status !== "active" && !problem.accepted_booking_id && (
          <button type="button" onClick={() => setDeleteOpen(true)} className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-[#D9AAA5] px-3.5 text-sm font-medium text-[#991B1B] hover:bg-[#FEF2F2]" data-testid="delete-request-button">
            <Trash2 size={15}/> Slet forespørgsel
          </button>
        )}
      </header>

      {deleteOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4" role="presentation">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl sm:p-6" role="dialog" aria-modal="true" aria-labelledby="delete-request-title">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="delete-request-title" className="text-xl font-semibold text-brand-ink">Slet forespørgslen?</h2>
                <p className="mt-2 text-sm leading-6 text-brand-body">Forespørgslen, prisforslag og tilhørende beskeder slettes permanent. Handlingen kan ikke fortrydes.</p>
              </div>
              <button type="button" onClick={() => !deleting && setDeleteOpen(false)} aria-label="Luk" className="rounded-full p-2 text-brand-muted hover:bg-brand-soft"><X size={18}/></button>
            </div>
            {deleteError && <p className="mt-4 text-sm text-[#991B1B]" role="alert">{deleteError}</p>}
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button type="button" disabled={deleting} onClick={() => setDeleteOpen(false)} className="min-h-11 rounded-lg border border-brand-line px-4 text-sm font-semibold text-brand-ink">Behold forespørgslen</button>
              <button type="button" disabled={deleting} onClick={deleteRequest} className="min-h-11 rounded-lg bg-[#B91C1C] px-4 text-sm font-semibold text-white disabled:opacity-50">
                {deleting ? "Sletter…" : "Slet permanent"}
              </button>
            </div>
          </div>
        </div>
      )}

      {hasNewResponse && (
        <section className="mt-6 rounded-2xl border border-brand-line-strong bg-brand-soft p-4 sm:p-5" data-testid="new-response-banner">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-fresh">Kræver din handling</div>
          <h2 className="mt-1 font-display text-xl font-semibold text-brand-ink">
            {unreadCount > 0 ? `Du har ${unreadCount} nye beskeder` : `Du har ${problem.proposal_count} nye prisforslag`}
          </h2>
          <p className="mt-1 text-sm text-[#4D6E64]">Læs svaret nedenfor og vælg dit næste trin.</p>
        </section>
      )}

      {agreementSelected && (
        <section className="mt-6 rounded-2xl border border-brand-line bg-white p-4 sm:p-5" data-testid="customer-agreement-progress">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Dit forløb</div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ["Ekspert valgt", true],
              ["Betaling", paymentDone],
              ["Tidspunkt", scheduleDone],
              ["Samtale", completed],
              ["Afslutning", completed],
            ].map(([label, done], index) => (
              <div key={label} className={`rounded-xl border px-3 py-3 ${done ? "border-brand-line-strong bg-brand-soft" : "border-brand-line bg-brand-wash"}`}>
                <div className={`text-[10px] font-semibold uppercase tracking-wider ${done ? "text-brand-fresh" : "text-brand-muted"}`}>Trin {index + 1}</div>
                <div className="mt-1 text-sm font-medium text-brand-ink">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!agreementSelected && (
        <section className={`mt-5 rounded-2xl border bg-brand-wash p-4 sm:p-5 ${hasNewResponse ? "border-brand-line-strong" : "border-brand-line"}`} data-testid="request-detail-proposals">
          <h2 className="font-display text-[21px] font-semibold text-brand-ink">{hasNewResponse ? "Svar fra eksperter" : "Prisforslag"}</h2>
          <p className="mt-1 text-sm text-brand-muted">{hasNewResponse ? "Sammenlign svarene og vælg den ekspert der passer bedst." : "Prisforslag vises her, når en ekspert har vurderet opgaven."}</p>
          <div className="mt-4">
            <ProblemProposals problemId={pid} onSelected={reload} onRequestOtherTime={requestOtherTime}/>
          </div>
        </section>
      )}

      {agreementSelected && (
        <section className="mt-5" aria-label="Næste trin">
          <VideoConsultationEntry
            problem={problem}
            role="customer"
            onOpenChat={async () => {
              await openChat(problem.accepted_expert_user_id);
              window.setTimeout(() => clarificationSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
            }}
            onProblemUpdated={(videoState) => setProblem((current) => ({
              ...current,
              status: videoState.problem_status,
              completion_status: videoState.completion_status,
              completed_at: videoState.completed_at,
              funds_state: videoState.funds_state,
              funds_release_at: videoState.funds_release_at,
              payment_status: videoState.payment_status ?? current.payment_status,
            }))}
          />
          {problem.status === "offer_accepted" && <AgreementScheduling
            problem={problem}
            className="mt-5"
            onUpdated={(schedule) => setProblem((current) => ({ ...current, ...schedule }))}
          />}
        </section>
      )}

      <section id="chat" ref={clarificationSectionRef} className="mt-5 scroll-mt-24 rounded-2xl border border-brand-line bg-white p-4 sm:p-5" data-testid="request-detail-clarification">
        <button type="button" onClick={() => chatOpen ? setChatOpen(false) : openChat()} className="flex w-full items-center justify-between gap-4 text-left" aria-expanded={chatOpen}>
          <div className="flex items-center gap-3">
            <MessageSquare size={18} className="text-brand-green"/>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-[17px] font-semibold text-brand-ink">Afklaring med eksperterne</h2>
                {unreadCount > 0 && <span className="rounded-md bg-[#E4F2E9] px-2 py-0.5 text-[10px] font-medium text-brand-fresh">{unreadCount} nye beskeder</span>}
              </div>
              <p className="mt-0.5 text-xs text-brand-muted">{chatOpen ? "Skjul samtalen" : "Åbn chat i fuld størrelse"}</p>
            </div>
          </div>
          <ChevronDown size={17} className={`text-brand-green transition-transform ${chatOpen ? "rotate-180" : ""}`}/>
        </button>

        {!chatOpen && latestThread && (
          <button type="button" onClick={() => openChat(latestThread.expert_user_id)} className="mt-4 flex w-full items-center gap-3 rounded-xl bg-brand-soft p-3 text-left">
            {latestThread.expert_photo ? (
              <img src={latestThread.expert_photo} alt="" className="h-9 w-9 rounded-full border border-brand-line object-cover"/>
            ) : (
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-line"><UserRound size={16} className="text-brand-muted"/></span>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-brand-ink">{latestThread.expert_name}</div>
              <div className="mt-0.5 truncate text-xs text-brand-body">{latestThread.last_message || "Åbn samtalen for at skrive en besked"}</div>
            </div>
            {(latestThread.unread_count || 0) > 0 && <span className="rounded-full bg-brand-fresh px-2 py-0.5 text-[9px] font-medium text-white">Ny</span>}
          </button>
        )}

        {!chatOpen && threads.length === 0 && (
          <div className="mt-4 rounded-xl bg-brand-soft p-3 text-sm text-brand-muted">Ingen afklaringsbeskeder endnu.</div>
        )}

        {chatOpen && (
          <div className="mt-4 border-t border-brand-line pt-4">
            {threads.length > 0 ? (
              <>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {threads.map((thread) => (
                    <button
                      key={thread.expert_user_id}
                      onClick={() => openChat(thread.expert_user_id)}
                      className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium ${
                        selectedExpertId === thread.expert_user_id
                          ? "border-brand-green bg-brand-soft text-brand-green"
                          : "border-brand-line bg-white text-brand-body"
                      }`}
                    >
                      {thread.expert_name}
                    </button>
                  ))}
                </div>
                {selectedThread && (
                  <ProblemClarification problemId={pid} role="customer" expertUserId={selectedThread.expert_user_id} className="mt-3"/>
                )}
              </>
            ) : (
              <div className="text-sm text-brand-muted">Ingen eksperter har startet en afklaring endnu.</div>
            )}
          </div>
        )}
      </section>


      <div className="mt-5">
        <RequestScopeAccordion problem={problem}/>
      </div>

      <CancellationAndSupport problem={problem} role="customer" onUpdated={(updates) => setProblem((current) => ({ ...current, ...updates }))}/>
      <ReviewPrompt problem={problem} onReviewed={reload}/>
    </div>
  );
}

